import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Layers, ClipboardList, Search, Flame, GraduationCap } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export default function Landing() {
  const { brand } = useAppSettings();
  const [packages, setPackages] = useState<any[]>([]);
  useEffect(() => {
    supabase.from("packages").select("*").eq("active", true).order("sort_order").then(({ data }) => setPackages(data || []));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-gradient-primary flex items-center justify-center shadow-glow overflow-hidden">
            {brand.logoUrl ? (
              <img src={brand.logoUrl} alt={brand.name} className="h-full w-full object-cover" />
            ) : (
              <GraduationCap className="h-5 w-5 text-primary-foreground" />
            )}
          </div>
          <div className="leading-tight">
            <div className="font-bold">{brand.name || "Nihongo"}</div>
            <div className="text-[10px] text-muted-foreground">{brand.tagline || "Master Japanese"}</div>
          </div>
        </Link>
        <nav className="flex items-center gap-2">
          <Link to="/about" className="text-sm px-3 py-2 hover:text-primary">About</Link>
          <a href="#pricing" className="text-sm px-3 py-2 hover:text-primary">Pricing</a>
          <ThemeToggle />
          <Button asChild variant="ghost"><Link to="/auth">Sign in</Link></Button>
          <Button asChild><Link to="/auth">Get started</Link></Button>
        </nav>
      </header>

      <main className="container mx-auto px-4">
        <section className="text-center py-16 md:py-24 space-y-6">
          <Badge variant="secondary" className="mb-2">日本語を学ぼう</Badge>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
            Master Japanese,<br />one card at a time.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Structured JLPT courses, spaced-repetition flashcards, practice tests, and a Jisho-powered dictionary - all in one place.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg"><Link to="/auth">Start learning - free</Link></Button>
            <Button asChild size="lg" variant="outline"><a href="#features">See features</a></Button>
          </div>
        </section>

        <section id="features" className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Feature icon={BookOpen} title="Structured courses" desc="JLPT N5 to N1 lessons with grammar, kana, and kanji." />
          <Feature icon={Layers} title="Smart flashcards" desc="Spaced-repetition (SM-2) keeps vocabulary in long-term memory." />
          <Feature icon={ClipboardList} title="Practice tests" desc="Timed quizzes with instant feedback and explanations." />
          <Feature icon={Search} title="Live dictionary" desc="Search any word, save favorites, see JLPT level and examples." />
        </section>

        <section className="py-16 text-center space-y-4">
          <Flame className="h-12 w-12 text-orange-500 mx-auto" />
          <h2 className="text-3xl font-bold">Build a daily streak</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Earn XP for every lesson, card, and test. Consistency beats cramming - 15 minutes a day will get you to fluency.
          </p>
        </section>

        <section id="pricing" className="py-16 space-y-8">
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold">Simple pricing</h2>
            <p className="text-muted-foreground">Choose what works for you.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {packages.map((p) => (
              <Card key={p.id} className={`h-full flex flex-col ${p.highlight ? "border-primary shadow-glow" : ""}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{p.name}</CardTitle>
                    {p.highlight && <Badge>Popular</Badge>}
                  </div>
                  <div className="text-3xl font-bold mt-2">
                    ${(p.price_cents / 100).toFixed(0)}
                    <span className="text-sm font-normal text-muted-foreground">/{p.billing_period}</span>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <ul className="text-sm space-y-2 mb-6">
                    {(p.features || []).map((f: string, i: number) => <li key={i}>✓ {f}</li>)}
                  </ul>
                  <Button asChild className="w-full mt-auto"><Link to="/auth">Get {p.name}</Link></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t mt-16">
        <div className="container mx-auto px-4 py-8 text-sm text-muted-foreground flex flex-wrap gap-4 items-center justify-between">
          <div>© {new Date().getFullYear()} {brand.name || "Nihongo"}</div>
          <div className="flex gap-4">
            <Link to="/about">About</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/refund-policy">Refunds</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }: any) {
  return (
    <Card className="h-full">
      <CardContent className="p-6 space-y-3">
        <Icon className="h-8 w-8 text-primary" />
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-muted-foreground">{desc}</div>
      </CardContent>
    </Card>
  );
}
